/**
 * Canvas Area Components
 * 
 * Reusable canvas wrapper components with consistent premium styling.
 */

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Panel, useReactFlow } from 'reactflow'
import { useState, useEffect } from 'react'
import { AlertTriangle, LayoutGrid, Loader2, Maximize2, Minus, Plus, Radio } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../hooks/useYCanvas'

interface CanvasContainerProps {
  children: React.ReactNode
  className?: string
}

/**
 * CanvasContainer - Wrapper for ReactFlow canvas with proper sizing
 */
export function CanvasContainer({ children, className }: CanvasContainerProps) {
  return (
    <div className={cn('flex-1 relative', className)}>
      <div className="absolute inset-0">
        {children}
      </div>
    </div>
  )
}

/**
 * CanvasControls - Custom zoom/fit controls with clean, minimal styling
 */
export function CanvasControls({ onArrange, arranging = false, arrangeDisabled = false, arrangeLoading = false, awaitingDecision = false, onKeepArrangement, onRestoreArrangement }: {
  onArrange?: () => void
  arranging?: boolean
  arrangeDisabled?: boolean
  /** Room not synced or canvas media still loading: disabled with an explanatory tooltip. */
  arrangeLoading?: boolean
  awaitingDecision?: boolean
  onKeepArrangement?: () => void
  onRestoreArrangement?: () => void
}) {
  const { zoomIn, zoomOut, fitView, getZoom } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const { t } = useTranslation('workspace')
  const [zoom, setZoom] = useState(100)

  useEffect(() => {
    const updateZoom = () => setZoom(Math.round(getZoom() * 100))
    updateZoom()
    const interval = setInterval(updateZoom, 100)
    return () => clearInterval(interval)
  }, [getZoom])

  return (
    <Panel position="bottom-left" className="ml-4 mb-4">
      <div className="flex items-center gap-2">
        {onArrange && (
          <div className="relative">
          {awaitingDecision && (
            <div role="dialog" aria-label={t('canvasArea.arrangeReview')} data-testid="arrange-review"
              className="absolute bottom-full left-0 mb-2 w-64 rounded-xl border border-border bg-surface-1 p-3 shadow-xl"
              onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); onKeepArrangement?.() } }}>
              <p className="text-sm text-foreground mb-3">{t('canvasArea.arrangeReview')}</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={onRestoreArrangement} data-testid="arrange-restore"
                  className="h-7 px-3 rounded-lg border border-input-border bg-surface-1 text-sm text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  {t('canvasArea.arrangeRestore')}
                </button>
                <button type="button" onClick={onKeepArrangement} data-testid="arrange-keep"
                  className="h-7 px-3 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  {t('canvasArea.arrangeKeep')}
                </button>
              </div>
            </div>
          )}
          {/* Disabled buttons swallow hover events in some browsers; the wrapper owns the tooltip. */}
          <span className="inline-flex" data-testid="arrange-canvas-tooltip"
            title={t(arranging ? 'canvasArea.arranging' : arrangeLoading && !arrangeDisabled ? 'canvasArea.arrangeLoading' : 'canvasArea.arrange')}>
          <button type="button" onClick={onArrange} disabled={arranging || arrangeDisabled || arrangeLoading}
            data-testid="arrange-canvas" aria-label={t(arrangeLoading && !arranging && !arrangeDisabled ? 'canvasArea.arrangeLoading' : 'canvasArea.arrange')} aria-busy={arranging}
            className="p-2 rounded-lg bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-surface-2 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {arranging ? <Loader2 className="w-4 h-4 animate-spin" /> : <LayoutGrid className="w-4 h-4" />}
          </button>
          </span>
          </div>
        )}
        <div className="bg-card border border-border rounded-lg flex flex-row items-center overflow-hidden">
          <button
            onClick={() => zoomOut()}
            className={cn(
              'p-2 hover:bg-muted transition-colors',
              'text-muted-foreground hover:text-foreground cursor-pointer'
            )}
            title={t('canvasArea.zoomOut')}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <div
            className="px-2.5 py-2 text-xxs font-mono text-muted-foreground text-center min-w-[44px] border-x border-border select-none"
            title={t('canvasArea.currentZoom', { zoom })}
          >
            {zoom}%
          </div>
          <button
            onClick={() => zoomIn()}
            className={cn(
              'p-2 hover:bg-muted transition-colors',
              'text-muted-foreground hover:text-foreground cursor-pointer'
            )}
            title={t('canvasArea.zoomIn')}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => fitView({ padding: 0.2, duration: 200 })}
            className={cn(
              'p-2 hover:bg-muted transition-colors border-l border-border',
              'text-muted-foreground hover:text-foreground cursor-pointer'
            )}
            title={t('canvasArea.fitToView')}
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </Panel>
  )
}

interface LoadingStateProps {
  message?: string
}

/**
 * LoadingState - Loading indicator for workspace
 */
export function LoadingState({ message }: LoadingStateProps) {
  const { t } = useTranslation('workspace')
  const displayMessage = message ?? t('canvasArea.loadingWorkspace')
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="flex flex-col items-center gap-4 animate-fade-in">
        <div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin" />
        <div className="text-muted-foreground text-sm">{displayMessage}</div>
      </div>
    </div>
  )
}

export type CanvasLoadStatusTone = 'loading' | 'slow' | 'delayed' | 'error'

export interface CanvasLoadStatus {
  id?: string
  tone: CanvasLoadStatusTone
  title: string
  detail: string
  step: string
}

export function getCanvasLoadStatus({
  connected,
  synced,
  ready,
  elapsedMs,
}: {
  connected: boolean
  synced: boolean
  ready: boolean
  elapsedMs: number
}): CanvasLoadStatus {
  const isVerySlow = elapsedMs >= 30000
  const isSlow = elapsedMs >= 8000

  if (!connected) {
    if (isVerySlow) {
      return {
        id: 'connectionDelayed',
        tone: 'delayed',
        title: 'Canvas connection delayed',
        detail: 'The live canvas room has not connected yet.',
        step: 'Waiting for Yjs WebSocket',
      }
    }
    return {
      id: isSlow ? 'connectingSlow' : 'connecting',
      tone: isSlow ? 'slow' : 'loading',
      title: isSlow ? 'Still connecting to canvas' : 'Connecting to canvas',
      detail: isSlow
        ? 'The server is responding slowly. This usually recovers automatically.'
        : 'Opening the live YDoc room.',
      step: 'Yjs WebSocket',
    }
  }

  if (!synced) {
    if (isVerySlow) {
      return {
        id: 'syncDelayed',
        tone: 'delayed',
        title: 'Canvas sync delayed',
        detail: 'The document is connected, but the initial graph has not arrived yet.',
        step: 'YDoc sync',
      }
    }
    return {
      id: isSlow ? 'syncingSlow' : 'syncing',
      tone: isSlow ? 'slow' : 'loading',
      title: isSlow ? 'Still syncing canvas' : 'Syncing canvas',
      detail: isSlow
        ? 'The document is connected but the initial graph has not arrived yet.'
        : 'Receiving the project graph from the server.',
      step: 'YDoc sync',
    }
  }

  if (!ready) {
    if (isVerySlow) {
      return {
        id: 'renderDelayed',
        tone: 'delayed',
        title: 'Canvas render delayed',
        detail: 'The document is synced, but rendering the graph is taking longer than usual.',
        step: 'Graph render',
      }
    }
    return {
      id: isSlow ? 'buildingSlow' : 'building',
      tone: isSlow ? 'slow' : 'loading',
      title: isSlow ? 'Still building canvas' : 'Building canvas',
      detail: isSlow
        ? 'The document is synced; rendering the graph is taking longer than usual.'
        : 'Rendering nodes, groups, and edges.',
      step: 'Graph render',
    }
  }

  return {
    id: 'preparingView',
    tone: 'loading',
    title: 'Preparing view',
    detail: 'Restoring the saved viewport.',
    step: 'Viewport',
  }
}

interface CanvasLoadOverlayProps {
  visible: boolean
  connected: boolean
  synced: boolean
  ready: boolean
  nodeCount: number
  resetKey: string
  error?: { message: string; reason?: string } | null
  /**
   * Multi-canvas keep-alive: when the loading overlay is suppressed for a warm
   * switch-back (already-synced Y.Doc), the shared ReactFlow `nodes` state can
   * still show the PREVIOUS canvas for a frame or two while `useYCanvas`
   * rebuilds. `quiet` renders a plain opaque cover (no spinner / progress card)
   * to hide that stale content until the active canvas has rendered.
   */
  quiet?: boolean
}

export function CanvasLoadOverlay({
  visible,
  connected,
  synced,
  ready,
  nodeCount,
  resetKey,
  error = null,
  quiet = false,
}: CanvasLoadOverlayProps) {
  const { t } = useTranslation('workspace')
  const [elapsedMs, setElapsedMs] = useState(0)

  useEffect(() => {
    setElapsedMs(0)
    if (!visible) return

    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [visible, resetKey])

  if (!visible) {
    // Warm switch-back: no loading card, just an opaque cover that masks the
    // previous canvas's nodes for the brief useYCanvas rebuild window.
    if (quiet) {
      return (
        <div
          className="pointer-events-none absolute inset-0 z-30 bg-background"
          data-testid="canvas-warm-cover"
          aria-hidden="true"
        />
      )
    }
    return null
  }

  const status: CanvasLoadStatus = error
    ? {
        id: 'error',
        tone: 'error',
        title: 'Canvas failed to load',
        detail: error.message,
        step: 'YDoc load error',
      }
    : getCanvasLoadStatus({ connected, synced, ready, elapsedMs })

  // RFC-1045 / T281165369: a transient-infra failure (surfaced only after the
  // retry budget is exhausted) is recoverable, NOT corruption — keep the whole
  // overlay off the "needs repair" framing (title + footer), consistent with the
  // transientInfra detail copy ("usually resolves on its own").
  const isTransientInfra = error?.reason === 'ydoc_load_transient_infra'
  const translatedTitle = status.id === 'error'
    ? isTransientInfra
      ? t('canvasArea.temporaryConnectionIssue')
      : t('canvasArea.canvasFailedToLoad')
    : status.id
      ? t(`canvasArea.status.${status.id}.title`)
      : status.title
  // Map a known YDoc failure reason to a localized message; fall back to the
  // raw error.message (English) only for unrecognized reasons. The reason codes
  // are stable (`ydoc_connect_failed` / `ydoc_load_failed` from useYDoc), so the
  // user sees localized copy instead of the raw hook English.
  const localizedErrorDetail =
    error?.reason === 'ydoc_connect_failed'
      ? t('canvasArea.ydocError.connectFailed')
      : error?.reason === 'ydoc_load_failed'
        ? t('canvasArea.ydocError.loadFailed')
        : error?.reason === 'ydoc_load_transient_infra'
          ? t('canvasArea.ydocError.transientInfra')
          : (error?.message ?? '')

  const translatedDetail = status.id === 'error'
    ? localizedErrorDetail
    : status.id
      ? t(`canvasArea.status.${status.id}.detail`)
      : status.detail
  const translatedStep = status.id === 'error'
    ? t('canvasArea.ydocLoadError')
    : status.id
      ? t(`canvasArea.status.${status.id}.step`)
      : status.step

  const elapsedSeconds = Math.floor(elapsedMs / 1000)
  const icon = status.tone === 'error'
    ? <AlertTriangle className="h-4 w-4" />
    : status.tone === 'delayed'
    ? <AlertTriangle className="h-4 w-4" />
    : status.tone === 'slow'
      ? <Radio className="h-4 w-4" />
      : <Loader2 className="h-4 w-4 animate-spin" />
  const toneClass = status.tone === 'error'
    ? 'text-destructive border-destructive/40 bg-destructive/10'
    : status.tone === 'delayed'
    ? 'text-warning border-warning/40 bg-warning/10'
    : status.tone === 'slow'
      ? 'text-primary border-primary/40 bg-primary/10'
      : 'text-muted-foreground border-border bg-surface-1/95'
  const phases: Array<readonly [string, boolean]> = [
    [t('canvasArea.connect'), connected],
    [t('canvasArea.sync'), synced],
    [t('canvasArea.build'), ready],
    [t('canvasArea.view'), false],
  ]

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      data-testid="canvas-load-overlay"
    >
      <div className="w-80 rounded-xl border border-border bg-surface-0/95 p-4 shadow-2xl">
        <div className="mb-3 flex items-center gap-3">
          <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg border', toneClass)}>
            {icon}
          </div>
          <div
            className="min-w-0"
            role={status.tone === 'error' ? 'alert' : 'status'}
            aria-live={status.tone === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
          >
            <div className="text-sm font-medium text-foreground">{translatedTitle}</div>
            <div className="text-xs font-medium text-muted-foreground">{translatedDetail}</div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          {phases.map(([label, done], index) => (
            <div key={String(label)} className="space-y-1">
              <div
                className={cn(
                  'h-1 rounded-full',
                  done ? 'bg-primary' : index === 3 && ready ? 'bg-primary/50' : 'bg-surface-2',
                )}
              />
              <div className="text-xxs text-text-muted">{label}</div>
            </div>
          ))}
        </div>

        <div
          className="mt-3 flex items-center justify-between border-t border-border-subtle pt-3 text-xxs text-text-muted"
          aria-hidden="true"
        >
          <span>{translatedStep}</span>
          <span>{status.tone === 'error' ? (isTransientInfra ? t('canvasArea.reloadToRetry') : t('canvasArea.repairRequired')) : t('canvasArea.elapsedNodes', { seconds: elapsedSeconds, nodeCount })}</span>
        </div>
      </div>
    </div>
  )
}

interface ErrorStateProps {
  title?: string
  message?: string
}

/**
 * ErrorState - Error indicator for workspace
 */
export function ErrorState({
  title,
  message,
}: ErrorStateProps) {
  const { t } = useTranslation('workspace')
  const displayTitle = title ?? t('canvasArea.projectNotFound')
  const displayMessage = message ?? t('canvasArea.projectNotFoundMessage')
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="flex flex-col items-center gap-3 text-center max-w-sm px-6 animate-fade-in">
        <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center">
          <svg
            className="w-6 h-6 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-sm font-medium text-foreground mb-1">{displayTitle}</h2>
          <p className="text-xs font-medium text-muted-foreground">{displayMessage}</p>
        </div>
      </div>
    </div>
  )
}
